import { useState, useEffect, useMemo, useRef, Fragment } from 'react'
import toast from 'react-hot-toast'
import { expenseApi, jobApi } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import { fileToReceiptDataUrl, dataUrlBytes } from '../utils/receiptImage'
import PlateField from '../components/common/PlateField'
import CarWashField from '../components/common/CarWashField'
import ReceiptViewer from '../components/common/ReceiptViewer'
import ReceiptPicker from '../components/common/ReceiptPicker'
import { useReceiptViewer } from '../hooks/useReceiptViewer'
import { splitExpense, carWashProblem, isForeign, amountFields, amountProblem } from '../utils/expenseMoney'
import AmountField, { ForeignAmountNote } from '../components/common/AmountField'

function currentMonth() {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

function today() {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

function money(n) {
    return (Number(n) || 0).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' })
}

function monthLabel(month) {
    const [y, m] = month.split('-').map(Number)
    return new Date(y, m - 1, 1).toLocaleDateString('en-CA', { month: 'long', year: 'numeric' })
}

const emptyForm = {
    cardNumber: '',
    expenseDate: today(),
    categoryId: '',
    vendor: '',
    jobNumber: '',
    plateNumber: '',
    amountPaid: '',
    currency: 'CAD',
    originalAmount: '',
    cadConfirmed: false,
    tipAmount: '',
    carWash: false,
    carWashAmount: '',
    hasTax: true,
    notes: '',
}

/**
 * Whether a category offers the car wash box - an admin setting, on for Fuel.
 * Never on a foreign receipt: with no tax to split, the whole receipt is the
 * expense under its category.
 */
function offersCarWash(category, values) {
    return category?.carWashCategoryId != null && !isForeign(values)
}

/** Same rule for the tip: it only matters for the tax split, which a foreign receipt does not have. */
function takesTip(category, values) {
    return category?.promptsTip === true && !isForeign(values)
}

/** The wash to send for a line: only when the box is ticked on a category that offers it. */
function carWashToSend(values, category) {
    return offersCarWash(category, values) && values.carWash ? Number(values.carWashAmount) || 0 : 0
}

/**
 * What a line has to satisfy before it is worth sending. Shared by the entry
 * form and by correcting a line that is already saved, so a rule cannot end up
 * enforced on the way in and forgotten on the way back.
 *
 * Returns the problem to show, or null.
 */
function validateExpense(values, category, taxRate) {
    const tipCategory = takesTip(category, values)
    const plateCategory = category?.usesPlate === true

    if (!String(values.cardNumber || '').trim()) return 'Card number is required'
    if (!values.expenseDate) return 'Transaction date is required'
    if (!values.categoryId) return 'Category is required'
    if (!String(values.vendor || '').trim()) return 'Vendor name is required'

    // A fuel receipt with no truck on it cannot be costed to anything, so the
    // plate is required wherever it replaces the job number.
    if (plateCategory && !String(values.plateNumber || '').trim()) return 'Plate number is required'

    const badAmount = amountProblem(values)
    if (badAmount) return badAmount
    const amount = Number(values.amountPaid)

    if (tipCategory && String(values.tipAmount || '').trim()) {
        const tip = Number(values.tipAmount)
        if (!Number.isFinite(tip) || tip < 0) return 'Enter a valid tip, or leave it blank'
        // The usual mistake is entering the bill total in the tip box.
        if (tip >= amount) return 'The tip must be less than the total charged to the card'
    }

    if (offersCarWash(category, values) && values.carWash) {
        return carWashProblem({ ...values, tipAmount: tipCategory ? values.tipAmount : 0, taxRate })
    }

    return null
}

export default function Expenses() {
    const { user } = useAuth()

    const [categories, setCategories] = useState([])
    const [taxRate, setTaxRate] = useState(0.13)
    const [currencies, setCurrencies] = useState(['USD'])
    const [jobs, setJobs] = useState([])
    const [expenses, setExpenses] = useState([])
    const [month, setMonth] = useState(currentMonth())
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)

    const [form, setForm] = useState(emptyForm)
    const [receipt, setReceipt] = useState(null)     // { dataUrl, name, bytes }
    const [processing, setProcessing] = useState(false)
    const [jobQuery, setJobQuery] = useState('')
    const [showJobs, setShowJobs] = useState(false)
    const { receipt: receiptView, loadingId: openingReceipt, open: openReceipt, close: closeReceipt } = useReceiptViewer()

    // A saved entry stays correctable by the person who filed it. Catching your
    // own typo should not need the office, and anything missed is still caught
    // when the month is checked before the report goes out.
    const [editing, setEditing] = useState(null)       // uuid being corrected
    const [editDraft, setEditDraft] = useState(null)
    const [editReceipt, setEditReceipt] = useState(null)
    const [editSaving, setEditSaving] = useState(false)

    const cameraRef = useRef(null)
    const fileRef = useRef(null)

    useEffect(() => {
        loadStatic()
    }, [])

    useEffect(() => {
        loadExpenses()
    }, [month])

    // The card number is per-employee, so prefill it and let them change it —
    // someone may carry two, and the report is per employee per card.
    useEffect(() => {
        if (user?.defaultCardNumber) {
            setForm(prev => (prev.cardNumber ? prev : { ...prev, cardNumber: user.defaultCardNumber }))
        }
    }, [user])

    async function loadStatic() {
        try {
            const [cats, jobList] = await Promise.all([
                expenseApi.getCategories(),
                jobApi.getAll(),
            ])
            if (cats.success) {
                setCategories(cats.categories || [])
                if (cats.taxRate > 0) setTaxRate(cats.taxRate)
                if (cats.currencies?.length) setCurrencies(cats.currencies)
            }
            if (jobList.success) setJobs(jobList.jobs || [])
        } catch {
            toast.error('Could not load categories')
        }
    }

    async function loadExpenses() {
        setLoading(true)
        try {
            const response = await expenseApi.getMine(month)
            setExpenses(response.success ? response.expenses || [] : [])
        } catch {
            toast.error('Could not load your expenses')
            setExpenses([])
        }
        setLoading(false)
    }

    function change(field, value) {
        setForm(prev => ({ ...prev, [field]: value }))
    }

    async function handleFile(file) {
        if (!file) return
        setProcessing(true)
        try {
            const dataUrl = await fileToReceiptDataUrl(file)
            setReceipt({ dataUrl, name: file.name || 'receipt', bytes: dataUrlBytes(dataUrl) })
        } catch (error) {
            toast.error(error.message || 'That receipt could not be processed')
        }
        setProcessing(false)
    }

    function startEdit(expense) {
        setEditing(expense.uuid)
        setEditReceipt(null)
        setEditDraft({
            cardNumber: expense.cardNumber || '',
            expenseDate: expense.expenseDate,
            categoryId: String(expense.categoryId),
            vendor: expense.vendor || '',
            jobNumber: expense.jobNumber || '',
            plateNumber: expense.plateNumber || '',
            amountPaid: String(expense.amountPaid),
            currency: expense.currency || 'CAD',
            originalAmount: expense.originalAmount != null ? String(expense.originalAmount) : '',
            cadConfirmed: expense.currency && expense.currency !== 'CAD' ? !expense.cadEstimated : false,
            tipAmount: expense.tipAmount > 0 ? String(expense.tipAmount) : '',
            carWash: expense.carWashAmount > 0,
            carWashAmount: expense.carWashAmount > 0 ? String(expense.carWashAmount) : '',
            hasTax: expense.hasTax,
            notes: expense.notes || '',
        })
    }

    function cancelEdit() {
        setEditing(null)
        setEditDraft(null)
        setEditReceipt(null)
    }

    /**
     * Attaches a picked image to a line that is already saved.
     *
     * Returns null, or the reason it did not work. Replacing deletes the old
     * image on the server, so it only ever runs on an explicit save - never the
     * moment the camera hands a photo back.
     */
    async function attachReceipt(uuid, picked) {
        try {
            const response = await expenseApi.saveReceipt(uuid, picked.dataUrl)
            if (response.success) return null
            return response.message || 'The change was saved, but the receipt could not be attached.'
        } catch {
            return 'The change was saved, but the receipt could not be attached.'
        }
    }

    async function saveEdit(expense) {
        const category = categories.find(c => String(c.id) === String(editDraft.categoryId))

        const problem = validateExpense(editDraft, category, taxRate)
        if (problem) {
            toast.error(problem)
            return
        }

        const usesPlate = category?.usesPlate === true

        // Correcting the date can move the line into another month, which takes
        // it off the list on screen. That is right - it belongs to a different
        // month now - but it needs saying, or it reads as a delete.
        const movedMonth = !editDraft.expenseDate.startsWith(month)

        setEditSaving(true)
        try {
            const response = await expenseApi.update(expense.uuid, {
                cardNumber: editDraft.cardNumber.trim(),
                expenseDate: editDraft.expenseDate,
                categoryId: Number(editDraft.categoryId),
                vendor: editDraft.vendor.trim(),
                // Both are always sent, so changing the category drops whichever
                // no longer applies instead of stranding it on the row.
                jobNumber: usesPlate ? '' : editDraft.jobNumber.trim(),
                plateNumber: usesPlate ? editDraft.plateNumber.trim() : '',
                // Amount, currency and HST together - see amountFields.
                ...amountFields(editDraft),
                tipAmount: takesTip(category, editDraft) ? Number(editDraft.tipAmount) || 0 : 0,
                // Always sent too, so unticking car wash actually removes it.
                carWashAmount: carWashToSend(editDraft, category),
                notes: editDraft.notes.trim(),
            })

            if (response.success) {
                // After the line, not before: the receipt file is named from the
                // date, vendor and amount on the row, so it should be named from
                // the corrected ones.
                const receiptProblem = editReceipt ? await attachReceipt(expense.uuid, editReceipt) : null

                cancelEdit()
                toast.success(movedMonth
                    ? `Moved to ${monthLabel(editDraft.expenseDate.slice(0, 7))} — it now appears under that month.`
                    : 'Updated')
                if (receiptProblem) toast.error(receiptProblem)
                loadExpenses()
            } else {
                toast.error(response.message || 'Could not save the change')
            }
        } catch {
            toast.error('Could not save the change')
        }
        setEditSaving(false)
    }

    const filteredJobs = useMemo(() => {
        const q = jobQuery.trim().toLowerCase()
        if (!q) return []
        return jobs
            .filter(j =>
                (j.workOrderNumber || '').toLowerCase().includes(q) ||
                (j.customer || '').toLowerCase().includes(q) ||
                (j.jobDescription || '').toLowerCase().includes(q))
            .slice(0, 20)
    }, [jobQuery, jobs])

    const monthTotal = useMemo(
        () => expenses.reduce((sum, e) => sum + (Number(e.amountPaid) || 0), 0),
        [expenses]
    )

    // Which categories offer a tip box is an admin setting, not a hardcoded id -
    // it is on for Food & Beverage out of the box and can be turned on for
    // anything else without a code change.
    const selectedCategory = useMemo(
        () => categories.find(c => String(c.id) === String(form.categoryId)),
        [categories, form.categoryId]
    )
    const tipCategory = takesTip(selectedCategory, form)

    // Fuel belongs to a truck rather than a work order, so those categories ask
    // for a plate in place of the job number.
    const plateCategory = selectedCategory?.usesPlate === true

    // A car wash bought at the pump: one receipt, filed as fuel plus a wash.
    const washCategory = offersCarWash(selectedCategory, form)
    const washFiledUnder = washCategory
        ? categories.find(c => c.id === selectedCategory.carWashCategoryId)?.name || 'its own category'
        : null

    // Switching category clears whichever field no longer applies, so a tip cannot
    // end up on a fuel line and a plate cannot end up on a meal just because the
    // box was filled in before the category was changed.
    useEffect(() => {
        if (!tipCategory && form.tipAmount) change('tipAmount', '')
    }, [tipCategory])

    useEffect(() => {
        if (!washCategory && (form.carWash || form.carWashAmount)) {
            setForm(prev => ({ ...prev, carWash: false, carWashAmount: '' }))
        }
    }, [washCategory])

    useEffect(() => {
        if (plateCategory && form.jobNumber) { change('jobNumber', ''); setJobQuery('') }
        if (!plateCategory && form.plateNumber) change('plateNumber', '')
    }, [plateCategory])

    // What the report will show. The tip is part of what went on the card and was
    // never taxed, so it comes off before the tax is worked back out. This mirrors
    // ExpenseMoney on the server, which remains the authority on what gets stored.
    const split = useMemo(() => splitExpense({
        amountPaid: form.amountPaid,
        tipAmount: tipCategory ? form.tipAmount : 0,
        carWashAmount: carWashToSend(form, selectedCategory),
        hasTax: form.hasTax,
        taxRate,
    }), [form.amountPaid, form.tipAmount, form.carWash, form.carWashAmount, form.hasTax,
        tipCategory, selectedCategory, taxRate])

    async function handleSave() {
        const problem = validateExpense(form, selectedCategory, taxRate)
        if (problem) {
            toast.error(problem)
            return
        }

        setSaving(true)
        try {
            const response = await expenseApi.create({
                cardNumber: form.cardNumber.trim(),
                expenseDate: form.expenseDate,
                categoryId: Number(form.categoryId),
                vendor: form.vendor.trim(),
                // Only one of the two ever goes with a line — whichever the
                // category asks for.
                jobNumber: plateCategory ? null : form.jobNumber.trim() || null,
                plateNumber: plateCategory ? form.plateNumber.trim() || null : null,
                ...amountFields(form),
                // Only ever sent for a category that asks for it, so a leftover
                // value cannot ride along after switching category.
                tipAmount: tipCategory ? Number(form.tipAmount) || 0 : 0,
                carWashAmount: carWashToSend(form, selectedCategory),
                notes: form.notes.trim() || null,
                receiptDataUrl: receipt?.dataUrl || null,
            })

            if (response.success) {
                toast.success('Expense saved')

                // Card number and date carry over — someone entering a stack of
                // receipts is usually working through one day on one card.
                setForm(prev => ({
                    ...emptyForm,
                    cardNumber: prev.cardNumber,
                    expenseDate: prev.expenseDate,
                    // A trip's receipts are all in the same currency.
                    currency: prev.currency,
                    hasTax: !isForeign(prev),
                }))
                setReceipt(null)
                setJobQuery('')
                if (cameraRef.current) cameraRef.current.value = ''
                if (fileRef.current) fileRef.current.value = ''

                // Only refetch when the new row belongs to the month on screen.
                if (form.expenseDate.startsWith(month)) loadExpenses()
            } else {
                toast.error(response.message || 'Could not save the expense')
            }
        } catch {
            toast.error('Could not save the expense')
        }
        setSaving(false)
    }

    return (
        <div>
            <div className="page-header">
                <h1 className="page-title">My Expenses</h1>
                <p className="text-blue-100 mt-1">
                    Enter a receipt and it goes straight onto your month.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* ---------------- Entry ---------------- */}
                <div className="card">
                    <h2 className="text-lg font-semibold text-gray-700 mb-4">Add an Expense</h2>

                    {/* Stacked on phones - two narrow inputs side by side is a
                        fiddly target, and the date picker in particular wants room. */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="form-group">
                            <label className="form-label">Card ending in *</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                maxLength={10}
                                value={form.cardNumber}
                                onChange={e => change('cardNumber', e.target.value)}
                                placeholder="1234"
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Date *</label>
                            <input
                                type="date"
                                value={form.expenseDate}
                                onChange={e => change('expenseDate', e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Category *</label>
                        <select value={form.categoryId} onChange={e => change('categoryId', e.target.value)}>
                            <option value="">Select a category…</option>
                            {categories.map(c => (
                                <option key={c.id} value={c.id}>{c.displayName}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Vendor *</label>
                        <input
                            type="text"
                            value={form.vendor}
                            onChange={e => change('vendor', e.target.value)}
                            placeholder="Petro-Canada"
                        />
                    </div>

                    {/* Fuel goes against a truck, everything else against a job.
                        Same slot in the form either way, so the layout does not
                        jump around when the category changes. */}
                    {plateCategory ? (
                        <div className="form-group">
                            <label className="form-label">Plate number *</label>
                            <PlateField
                                value={form.plateNumber}
                                onChange={v => change('plateNumber', v)}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Which vehicle was fuelled — pick
                                "Other" to type one that is not on it.
                            </p>
                        </div>
                    ) : (
                    /* Job number is validated against the real job list, unlike the
                       free-text box in the old app. */
                    <div className="form-group relative">
                        <label className="form-label">Job number</label>
                        {form.jobNumber ? (
                            <div className="flex items-center gap-2">
                                <span className="status-badge status-submitted">{form.jobNumber}</span>
                                <button
                                    type="button"
                                    className="text-sm text-gray-500 hover:text-rdi-red"
                                    onClick={() => { change('jobNumber', ''); setJobQuery('') }}
                                >
                                    change
                                </button>
                            </div>
                        ) : (
                            <>
                                <input
                                    type="text"
                                    value={jobQuery}
                                    onChange={e => { setJobQuery(e.target.value); setShowJobs(true) }}
                                    onFocus={() => setShowJobs(true)}
                                    placeholder="Search job or customer (optional)"
                                />
                                {showJobs && filteredJobs.length > 0 && (
                                    <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                        {filteredJobs.map(job => (
                                            <button
                                                key={job.workOrderNumber}
                                                type="button"
                                                className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                                                onClick={() => {
                                                    change('jobNumber', job.workOrderNumber)
                                                    setJobQuery('')
                                                    setShowJobs(false)
                                                }}
                                            >
                                                <span className="font-semibold text-rdi-blue">{job.workOrderNumber}</span>
                                                <span className="text-gray-600 text-sm ml-2">{job.customer}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    )}

                    <div className="form-group">
                        <AmountField value={form} onChange={setForm} currencies={currencies} />
                    </div>

                    {washCategory && (
                        <div className="form-group">
                            <CarWashField
                                value={form}
                                onChange={setForm}
                                taxRate={taxRate}
                                filedUnder={washFiledUnder}
                                mainLabel={selectedCategory.name}
                            />
                        </div>
                    )}

                    {/* Meals only. A tip is part of what the card was charged but
                        no tax was charged on it, so it has to be entered separately
                        or the report claims HST that was never paid. */}
                    {tipCategory && (
                        <div className="form-group">
                            <label className="form-label">Tip included</label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                inputMode="decimal"
                                value={form.tipAmount}
                                onChange={e => change('tipAmount', e.target.value)}
                                placeholder="0.00"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Part of the amount above, not on top of it. Leave blank if there was no tip.
                            </p>

                            {split.paid > 0 && (
                                <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm space-y-1">
                                    <div className="flex justify-between text-gray-600">
                                        <span>Meal before tax</span>
                                        <span>{money(split.preTax)}</span>
                                    </div>
                                    <div className="flex justify-between text-gray-600">
                                        <span>HST</span>
                                        <span>{money(split.tax)}</span>
                                    </div>
                                    <div className="flex justify-between text-gray-600">
                                        <span>Tip (no tax)</span>
                                        <span>{money(split.tip)}</span>
                                    </div>
                                    <div className="flex justify-between font-semibold text-gray-900 pt-1 border-t">
                                        <span>Charged to the card</span>
                                        <span>{money(split.paid)}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="form-group">
                        <label className="form-label">Note</label>
                        <input
                            type="text"
                            value={form.notes}
                            onChange={e => change('notes', e.target.value)}
                            placeholder="Optional — what it was for"
                        />
                    </div>

                    {/* ---- Receipt ---- */}
                    <div className="form-group">
                        <label className="form-label">Receipt</label>

                        <div className="flex flex-wrap items-center gap-2">
                            <label className="btn btn-secondary cursor-pointer">
                                📷 Take Photo
                                <input
                                    ref={cameraRef}
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    style={{ display: 'none' }}
                                    onChange={e => handleFile(e.target.files?.[0])}
                                />
                            </label>

                            <label className="btn btn-secondary cursor-pointer">
                                🖼️ Choose File
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept="image/*,application/pdf"
                                    style={{ display: 'none' }}
                                    onChange={e => handleFile(e.target.files?.[0])}
                                />
                            </label>

                            {processing && <span className="text-sm text-gray-500">Processing…</span>}
                        </div>

                        {receipt && (
                            <div className="mt-3 flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                {receipt.dataUrl.startsWith('data:application/pdf') ? (
                                    <div className="w-16 h-16 flex items-center justify-center bg-white border rounded text-2xl">
                                        📄
                                    </div>
                                ) : (
                                    <img
                                        src={receipt.dataUrl}
                                        alt="Receipt preview"
                                        className="w-16 h-16 object-cover rounded border"
                                    />
                                )}
                                <div className="flex-1 text-sm">
                                    <p className="font-medium text-gray-700 break-all">{receipt.name}</p>
                                    <p className="text-gray-500">{Math.round(receipt.bytes / 1024)} KB after resizing</p>
                                    <button
                                        type="button"
                                        className="text-rdi-red hover:underline mt-1"
                                        onClick={() => {
                                            setReceipt(null)
                                            if (cameraRef.current) cameraRef.current.value = ''
                                            if (fileRef.current) fileRef.current.value = ''
                                        }}
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                        )}

                        <p className="text-xs text-gray-500 mt-2">
                            Photos are resized to about 1600px before they are sent, so they stay small.
                        </p>
                    </div>

                    <button
                        className="btn btn-primary btn-block mt-2"
                        onClick={handleSave}
                        disabled={saving || processing}
                    >
                        {saving ? 'Saving…' : 'Save Expense'}
                    </button>
                </div>

                {/* ---------------- Own list ---------------- */}
                <div className="card">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-700">{monthLabel(month)}</h2>
                        <input
                            type="month"
                            value={month}
                            onChange={e => setMonth(e.target.value)}
                            style={{ width: 'auto' }}
                        />
                    </div>

                    {loading ? (
                        <div className="flex justify-center py-8"><div className="spinner w-8 h-8" /></div>
                    ) : expenses.length === 0 ? (
                        <p className="text-gray-500 text-center py-8">Nothing entered for this month yet.</p>
                    ) : (
                        <>
                            {/* Phones: one row per entry, stacked. A category like
                                "Vehicle Repair & Maintenance" pushes the table off
                                the side otherwise - and this is the screen employees
                                actually use on a phone. */}
                            <ul className="md:hidden divide-y">
                                {expenses.map(e => (
                                    <li key={e.uuid} className="py-3">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0 flex-1">
                                                <p className="font-medium text-gray-900 break-words">{e.vendor}</p>
                                                <p className="text-sm text-gray-500">
                                                    {e.expenseDate} · {e.categoryName}
                                                </p>
                                                {(e.jobNumber || e.plateNumber) && (
                                                    <p className="text-sm text-gray-400 break-words">
                                                        {e.jobNumber || e.plateNumber}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="text-right shrink-0">
                                                <p className="font-semibold text-gray-900 whitespace-nowrap">
                                                    {money(e.amountPaid)}
                                                </p>
                                                <ForeignAmountNote expense={e} className="block" />
                                                {e.tipAmount > 0 && (
                                                    <p className="text-xs text-gray-500 whitespace-nowrap">
                                                        incl. {money(e.tipAmount)} tip
                                                    </p>
                                                )}
                                                {e.carWashAmount > 0 && (
                                                    <p className="text-xs text-gray-500 whitespace-nowrap">
                                                        incl. {money(e.carWashPaid)} car wash
                                                    </p>
                                                )}
                                                {e.hasReceipt ? (
                                                    <button
                                                        onClick={() => openReceipt(e)}
                                                        disabled={openingReceipt === e.uuid}
                                                        className="text-rdi-blue text-sm hover:underline disabled:opacity-50"
                                                    >
                                                        {openingReceipt === e.uuid ? 'Opening…' : '📎 Receipt'}
                                                    </button>
                                                ) : (
                                                    <span className="text-gray-300 text-sm">No receipt</span>
                                                )}

                                                {editing !== e.uuid && (
                                                    <div className="flex items-center justify-end gap-3 mt-1">
                                                        {/* Both open the same form. A forgotten photo is the
                                                            common correction, so on the row it gets named
                                                            rather than hidden behind the word "edit". */}
                                                        {!e.hasReceipt && (
                                                            <button
                                                                type="button"
                                                                className="text-rdi-blue text-sm hover:underline"
                                                                onClick={() => startEdit(e)}
                                                            >
                                                                📷 Add receipt
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            className="text-rdi-blue text-sm font-medium hover:underline"
                                                            onClick={() => startEdit(e)}
                                                        >
                                                            Edit
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {editing === e.uuid && editDraft && (
                                            <EditExpense
                                                expense={e}
                                                value={editDraft}
                                                onChange={setEditDraft}
                                                categories={categories}
                                                jobs={jobs}
                                                receipt={editReceipt}
                                                onReceiptChange={setEditReceipt}
                                                onViewReceipt={() => openReceipt(e)}
                                                viewingReceipt={openingReceipt === e.uuid}
                                                onSave={() => saveEdit(e)}
                                                onCancel={cancelEdit}
                                                saving={editSaving}
                                                taxRate={taxRate}
                                                currencies={currencies}
                                            />
                                        )}
                                    </li>
                                ))}
                            </ul>

                            <div className="md:hidden flex justify-between font-semibold mt-3 pt-3 border-t">
                                <span>{expenses.length} entr{expenses.length === 1 ? 'y' : 'ies'}</span>
                                <span>{money(monthTotal)}</span>
                            </div>

                            <div className="hidden md:block overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-gray-500 border-b">
                                            <th className="py-2">Date</th>
                                            <th className="py-2">Vendor</th>
                                            <th className="py-2">Category</th>
                                            <th className="py-2 text-right">Paid</th>
                                            <th className="py-2 text-center">Rcpt</th>
                                            <th className="py-2" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {expenses.map(e => (
                                            <Fragment key={e.uuid}>
                                                <tr className="border-b border-gray-100">
                                                    <td className="py-2 whitespace-nowrap">{e.expenseDate}</td>
                                                    <td className="py-2">
                                                        {e.vendor}
                                                        {(e.jobNumber || e.plateNumber) && (
                                                            <span className="text-gray-400 ml-1">
                                                                · {e.jobNumber || e.plateNumber}
                                                            </span>
                                                        )}
                                                        {e.tipAmount > 0 && (
                                                            <span className="text-gray-400 ml-1">
                                                                · incl. {money(e.tipAmount)} tip
                                                            </span>
                                                        )}
                                                        {e.carWashAmount > 0 && (
                                                            <span className="text-gray-400 ml-1">
                                                                · incl. {money(e.carWashPaid)} car wash
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="py-2 text-gray-600">{e.categoryName}</td>
                                                    <td className="py-2 text-right whitespace-nowrap">
                                                        {money(e.amountPaid)}
                                                        <ForeignAmountNote expense={e} className="block" />
                                                    </td>
                                                    <td className="py-2 text-center">
                                                        {e.hasReceipt ? (
                                                            <button
                                                                onClick={() => openReceipt(e)}
                                                                disabled={openingReceipt === e.uuid}
                                                                className="text-rdi-blue hover:underline disabled:opacity-50"
                                                                title="View receipt"
                                                            >
                                                                {openingReceipt === e.uuid ? '…' : '📎'}
                                                            </button>
                                                        ) : (
                                                            <span className="text-gray-300">—</span>
                                                        )}
                                                    </td>
                                                    <td className="py-2 text-right">
                                                        {editing !== e.uuid && (
                                                            <button
                                                                type="button"
                                                                className="text-rdi-blue hover:underline"
                                                                onClick={() => startEdit(e)}
                                                            >
                                                                Edit
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>

                                                {editing === e.uuid && editDraft && (
                                                    <tr className="border-b border-gray-100 bg-blue-50">
                                                        <td colSpan={6} className="py-3">
                                                            <EditExpense
                                                                expense={e}
                                                                value={editDraft}
                                                                onChange={setEditDraft}
                                                                categories={categories}
                                                                jobs={jobs}
                                                                receipt={editReceipt}
                                                                onReceiptChange={setEditReceipt}
                                                                onViewReceipt={() => openReceipt(e)}
                                                                viewingReceipt={openingReceipt === e.uuid}
                                                                onSave={() => saveEdit(e)}
                                                                onCancel={cancelEdit}
                                                                saving={editSaving}
                                                                taxRate={taxRate}
                                                                currencies={currencies}
                                                            />
                                                        </td>
                                                    </tr>
                                                )}
                                            </Fragment>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="font-semibold">
                                            <td className="py-2" colSpan={3}>
                                                {expenses.length} entr{expenses.length === 1 ? 'y' : 'ies'}
                                            </td>
                                            <td className="py-2 text-right">{money(monthTotal)}</td>
                                            <td />
                                            <td />
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                            <p className="text-xs text-gray-500 mt-4">
                                Anything here can be corrected after it is saved — a wrong amount, the
                                category, or a receipt you forgot to photograph. The office also checks every
                                line before the month's report goes out, so a mistake you miss still gets caught.
                            </p>
                        </>
                    )}
                </div>
            </div>

            <ReceiptViewer receipt={receiptView} onClose={closeReceipt} />
        </div>
    )
}

/**
 * Corrects an entry that is already saved, receipt included.
 *
 * Sits under the line it belongs to on a phone and in a row of its own on a
 * desktop, so the entry stays on screen while it is being fixed - and so the
 * camera, which takes over the whole screen on a phone, comes back to something
 * recognisable. Coming back to an unlabelled form is how the wrong receipt ends
 * up on the wrong line.
 *
 * The job box is the same searched list as the entry form rather than free
 * text: a job number that does not exist is refused by the server anyway, and
 * people know the job by the customer on it.
 */
function EditExpense({
    expense,
    value,
    onChange,
    categories,
    jobs,
    receipt,
    onReceiptChange,
    onViewReceipt,
    viewingReceipt,
    onSave,
    onCancel,
    saving,
    taxRate,
    currencies,
}) {
    const [jobQuery, setJobQuery] = useState('')
    const [showJobs, setShowJobs] = useState(false)

    const set = (key, v) => onChange({ ...value, [key]: v })

    const category = categories.find(c => String(c.id) === String(value.categoryId))
    const tipCategory = takesTip(category, value)
    const plateCategory = category?.usesPlate === true
    const washCategory = offersCarWash(category, value)

    const matches = useMemo(() => {
        const q = jobQuery.trim().toLowerCase()
        if (!q) return []
        return jobs
            .filter(j =>
                (j.workOrderNumber || '').toLowerCase().includes(q) ||
                (j.customer || '').toLowerCase().includes(q) ||
                (j.jobDescription || '').toLowerCase().includes(q))
            .slice(0, 20)
    }, [jobQuery, jobs])

    return (
        <div className="mt-3 p-3 bg-blue-50 rounded-lg text-left">
            <p className="text-sm font-semibold text-gray-800 mb-3">
                Correcting {expense.vendor} · {expense.expenseDate}
            </p>

            <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label className="form-label">Card ending in *</label>
                        <input
                            type="text"
                            inputMode="numeric"
                            maxLength={10}
                            value={value.cardNumber}
                            onChange={e => set('cardNumber', e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="form-label">Date *</label>
                        <input
                            type="date"
                            value={value.expenseDate}
                            onChange={e => set('expenseDate', e.target.value)}
                        />
                    </div>
                </div>

                <div>
                    <label className="form-label">Category *</label>
                    <select value={value.categoryId} onChange={e => set('categoryId', e.target.value)}>
                        <option value="">Select a category…</option>
                        {categories.map(c => (
                            <option key={c.id} value={c.id}>{c.displayName}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="form-label">Vendor *</label>
                    <input
                        type="text"
                        value={value.vendor}
                        onChange={e => set('vendor', e.target.value)}
                    />
                </div>

                {plateCategory ? (
                    <div>
                        <label className="form-label">Plate number *</label>
                        <PlateField
                            value={value.plateNumber}
                            onChange={v => set('plateNumber', v)}
                        />
                    </div>
                ) : (
                    <div className="relative">
                        <label className="form-label">Job number</label>
                        {value.jobNumber ? (
                            <div className="flex items-center gap-2">
                                <span className="status-badge status-submitted">{value.jobNumber}</span>
                                <button
                                    type="button"
                                    className="text-sm text-gray-500 hover:text-rdi-red"
                                    onClick={() => { set('jobNumber', ''); setJobQuery('') }}
                                >
                                    change
                                </button>
                            </div>
                        ) : (
                            <>
                                <input
                                    type="text"
                                    value={jobQuery}
                                    onChange={e => { setJobQuery(e.target.value); setShowJobs(true) }}
                                    onFocus={() => setShowJobs(true)}
                                    placeholder="Search job or customer (optional)"
                                />
                                {showJobs && matches.length > 0 && (
                                    <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                        {matches.map(job => (
                                            <button
                                                key={job.workOrderNumber}
                                                type="button"
                                                className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                                                onClick={() => {
                                                    set('jobNumber', job.workOrderNumber)
                                                    setJobQuery('')
                                                    setShowJobs(false)
                                                }}
                                            >
                                                <span className="font-semibold text-rdi-blue">{job.workOrderNumber}</span>
                                                <span className="text-gray-600 text-sm ml-2">{job.customer}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                <AmountField value={value} onChange={onChange} currencies={currencies} help={false} />

                {washCategory && (
                    <CarWashField
                        value={value}
                        onChange={onChange}
                        taxRate={taxRate}
                        filedUnder={categories.find(c => c.id === category.carWashCategoryId)?.name || 'its own category'}
                        mainLabel={category.name}
                    />
                )}

                {tipCategory && (
                    <div>
                        <label className="form-label">Tip included</label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            inputMode="decimal"
                            value={value.tipAmount}
                            onChange={e => set('tipAmount', e.target.value)}
                            placeholder="0.00"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Part of the amount above, not on top of it.
                        </p>
                    </div>
                )}

                <div>
                    <label className="form-label">Note</label>
                    <input
                        type="text"
                        value={value.notes}
                        onChange={e => set('notes', e.target.value)}
                        placeholder="Optional — what it was for"
                    />
                </div>

                <div>
                    <label className="form-label">Receipt</label>
                    <ReceiptPicker
                        value={receipt}
                        onChange={onReceiptChange}
                        hasExisting={expense.hasReceipt}
                        onViewExisting={onViewReceipt}
                        viewingExisting={viewingReceipt}
                    />
                </div>
            </div>

            <div className="flex gap-2 mt-4">
                <button className="btn btn-primary flex-1" onClick={onSave} disabled={saving}>
                    {saving ? 'Saving…' : 'Save changes'}
                </button>
                <button className="btn btn-secondary flex-1" onClick={onCancel} disabled={saving}>
                    Cancel
                </button>
            </div>
        </div>
    )
}
